<!--
 * @Description: 项目订单提现。供应商提现先选择订单
 * @Date: 2025-05-30 13:56:59
-->
<template>
  <BaseDialog
    title="项目订单提现"
    width="900px"
    :model-value="true"
    :show-close="true"
    :close-on-press-escape="false"
    :close-on-click-modal="false"
  >
    <BaseTextContent>
      <XrkTable
        ref="xrkTableRef"
        :getListAjax="getListAjax"
        :searchConfig="searchConfig"
        :tableConfig="tableConfig"
      >
        <template #tableBefore>
          提现金额 {{ formatter.fen2yuan(withdrawAmount) }}元
        </template>
      </XrkTable>
    </BaseTextContent>
    <template #footer>
      <BaseButton type="default" @click="onCancel">关闭</BaseButton>
      <BaseButton type="primary" :disabled="!withdrawAmount" @click="onConfirm">
        下一步
      </BaseButton>
    </template>
  </BaseDialog>
</template>

<script lang="tsx" setup>
import { ref, computed, watch } from 'vue';
import {
  XrkTable,
  XrkTableProps,
  useTableColumn,
  useSelectColumn,
  BaseButton,
  useCreateModal,
  BaseDialog,
  BaseTextContent
} from 'xrk-components';
import { formatter } from '@/utils';
import { DITHDRAWAL_STATUS } from '@/common/constants/bank';
import { RECEIVE_STATUS } from '@/common/constants/order';
import {
  querySupplierWithdrawList,
  getSupplierWithdrawAmount
} from '../data-source/apis';
import AccountMainWithdrawalDialog from './account-main-withdrawal-dialog.vue';

const emits = defineEmits(['success']);
const props = withDefaults(
  defineProps<{
    remove?: () => {};
  }>(),
  {}
);

const xrkTableRef = ref(null);
const withdrawAmount = ref(0);

const selections = computed(() => {
  return xrkTableRef.value?.getSelectionRows?.() || [];
});
const idList = computed(() => {
  return selections.value.map(item => item.id);
});

watch(
  () => selections.value,
  async v => {
    if (v.length) {
      const { data } = await getSupplierWithdrawAmount({
        idList: idList.value
      });
      withdrawAmount.value = data.withdrawAmount;
    } else {
      withdrawAmount.value = 0;
    }
  }
);

const getListAjax = async params => {
  return querySupplierWithdrawList(params);
};

const onCancel = () => {
  props.remove();
};
const onConfirm = () => {
  useCreateModal(AccountMainWithdrawalDialog, {
    amount: withdrawAmount.value,
    idList: idList.value,
    functionCode: 3,
    onSuccess() {
      emits('success');
      props.remove();
    }
  });
};

const searchConfig = ref<XrkTableProps['searchConfig']>({
  searchBtn: {
    show: true,
    text: '搜索'
  },
  resetBtn: {
    show: false,
    text: '重置'
  },
  searchColumns: [
    useSelectColumn({
      prop: 'status',
      label: '提现状态',
      config: {
        placeholder: '请选择',
        options: DITHDRAWAL_STATUS
      }
    })
  ]
});

const tableConfig = ref<XrkTableProps['tableConfig']>({
  tableColumns: [
    useTableColumn({
      type: '_selection',
      prop: 'id',
      align: 'center',
      fixed: 'left',
      width: 110,
      selectionDisabled: row => {
        const {
          price,
          isServicePriceWithdraw,
          serviceWithdrawStatus,
          payStatus
        } = row;
        if (!price || !isServicePriceWithdraw) return true;
        if (payStatus != 2) return true; // 订单未支付完成的不可提现
        if (![0, 3].includes(serviceWithdrawStatus)) return true; // 服务费未处于未提现和失败的不可提现
        return false;
      }
    }),
    useTableColumn({
      type: 'index',
      label: '序号',
      width: '80px'
    }),
    useTableColumn({
      label: '订单编号',
      prop: 'orderNo',
      width: '180px'
    }),
    useTableColumn({
      label: '终端支付状态',
      prop: 'payStatus',
      width: '180px',
      formatter: ({ cellValue }) =>
        formatter.defaultFormat(
          cellValue,
          () => RECEIVE_STATUS[cellValue].label
        )
    }),
    useTableColumn({
      label: '终端结算金额',
      prop: 'projectPayPrice',
      width: '180px',
      formatter: ({ cellValue }) => formatter.fen2yuan(cellValue)
    }),
    useTableColumn({
      label: '可提现服务费',
      prop: 'price',
      width: '180px',
      formatter: ({ cellValue }) => formatter.fen2yuan(cellValue)
    }),
    useTableColumn({
      label: '提现状态',
      prop: 'serviceWithdrawStatus',
      formatter: ({ cellValue }) =>
        formatter.defaultFormat(
          cellValue,
          () => DITHDRAWAL_STATUS[cellValue].label
        )
    }),

    useTableColumn({
      label: '到账时间',
      prop: 'finishPayAt',
      width: '180px',
      formatter: ({ cellValue }) => formatter.date(cellValue)
    })
  ]
});
</script>
<style lang="scss">
@import '@/styles/mixin.scss';

.account__table__before {
  padding: 34px 0;
  margin-bottom: 16px;
  background: #f6f6f6;
  border-radius: 4px 4px 4px 4px;
  border: 1px solid #d8dce5;
  @include flex_jc_ai(space-around);

  div {
    font-weight: 500;
    font-size: 18px;
  }

  .account__table__before--selected {
    font-weight: 400;
    font-size: 14px;
    color: #636363;
    span {
      color: #2c82ea;
    }
  }
}
</style>
